import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Download, Mail, MessageSquare, Send, Loader2, Trash2 } from "lucide-react";
import SmartOfferRecommender from "@/components/SmartOfferRecommender";
import TierHistoryCard from "@/components/TierHistoryCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Offers() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [offers, setOffers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [signDialog, setSignDialog] = useState(null); // offer being sent
  const [sellerEmail, setSellerEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () =>
    api.get("/offers").then((r) => setOffers(r.data));

  useEffect(() => {
    load();
  }, []);

  const downloadPdf = async (o) => {
    try {
      const pdf = await api.get(`/offers/${o.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `offer_${o.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const sendForSignature = async () => {
    if (!signDialog) return;
    setSending(true);
    try {
      await api.post(`/offers/${signDialog.id}/send-for-signature`, {
        seller_email: sellerEmail || null,
      });
      toast.success("Sent for signature");
      setSignDialog(null);
      setSellerEmail("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "";
      if (msg.includes("not connected")) {
        toast.error("Connect DocuSign in Settings first");
      } else {
        toast.error(msg || "Send failed");
      }
    } finally {
      setSending(false);
    }
  };

  const deleteOffer = async (o) => {
    if (!window.confirm(
      `Delete this offer for ${money(o.purchase_price)}?\n\n` +
      `This removes the offer permanently. The underlying property is kept.`,
    )) return;
    setDeletingId(o.id);
    try {
      await api.delete(`/offers/${o.id}`);
      toast.success("Offer deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="offers-page">
      <div className="reveal">
        <span className="app-eyebrow">/ Deals</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">
            Offers
          </h1>
      </div>

      <div className="mt-6 reveal reveal-2">
        <TierHistoryCard />
      </div>

      <div className="mt-6 reveal reveal-2">
        <SmartOfferRecommender />
      </div>

      <div className="card-flat mt-6 reveal reveal-2">
        <div className="overflow-x-auto">
          <table className="w-full data-table text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3">Property</th>
                <th className="text-right px-5 py-3">Purchase Price</th>
                <th className="text-right px-5 py-3">Earnest</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-10 text-center text-muted-foreground">
                    No offers yet. Generate one from Property Lookup.
                  </td>
                </tr>
              )}
              {offers.map((o) => (
                <tr key={o.id} className="border-b border-border hover:bg-secondary/40">
                  <td className="px-5 py-3 truncate max-w-[260px] font-mono">
                    {o.property_id.slice(0, 8)}…
                  </td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">
                    {money(o.purchase_price)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono">
                    {money(o.earnest_money)}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="rounded-sm"
                        onClick={() => setPreview({ ...o, mode: "email" })}
                        data-testid={`preview-email-${o.id}`}
                      >
                        <Mail size={12} className="mr-1" />
                        Email
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="rounded-sm"
                        onClick={() => setPreview({ ...o, mode: "sms" })}
                        data-testid={`preview-sms-${o.id}`}
                      >
                        <MessageSquare size={12} className="mr-1" />
                        SMS
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-sm"
                        onClick={() => downloadPdf(o)}
                        data-testid={`download-pdf-${o.id}`}
                      >
                        <Download size={12} className="mr-1" />
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant={o.docusign_envelope_id ? "secondary" : "default"}
                        className="rounded-sm"
                        onClick={() => {
                          setSignDialog(o);
                          setSellerEmail("");
                        }}
                        data-testid={`send-sign-${o.id}`}
                        disabled={o.docusign_status === "completed"}
                      >
                        <Send size={12} className="mr-1" />
                        {o.docusign_status
                          ? o.docusign_status.replace("_", " ")
                          : "Sign"}
                      </Button>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => deleteOffer(o)}
                          disabled={deletingId === o.id}
                          data-testid={`delete-offer-${o.id}`}
                          title="Delete this offer (admin)"
                        >
                          {deletingId === o.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="offer-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {preview?.mode === "email" ? "Email Preview" : "SMS Preview"}
            </DialogTitle>
          </DialogHeader>
          {preview?.mode === "email" ? (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Subject
                </div>
                <div className="font-medium">{preview?.email_subject}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Body
                </div>
                <div className="text-sm whitespace-pre-wrap">
                  {preview?.email_body}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                SMS
              </div>
              <div className="text-sm whitespace-pre-wrap">{preview?.sms_body}</div>
            </div>
          )}
          <Button
            onClick={() => {
              navigator.clipboard.writeText(
                preview?.mode === "email"
                  ? `${preview.email_subject}\n\n${preview.email_body}`
                  : preview?.sms_body || "",
              );
              toast.success("Copied to clipboard");
            }}
            className="rounded-sm"
            data-testid="copy-message-button"
          >
            Copy
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!signDialog} onOpenChange={(v) => !v && setSignDialog(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle>Send Offer for E-Signature</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            The buyer signs first (using the email in your Buyer Profile),
            then the seller receives the DocuSign email. Leave blank to have
            only the buyer sign.
          </p>
          <div>
            <Label>Seller Email (optional)</Label>
            <Input
              value={sellerEmail}
              onChange={(e) => setSellerEmail(e.target.value)}
              placeholder="seller@example.com"
              className="field-sharp mt-1"
              data-testid="sign-seller-email"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={sendForSignature}
              disabled={sending}
              className="rounded-sm"
              data-testid="send-sign-confirm"
            >
              {sending ? (
                <><Loader2 className="animate-spin mr-2" size={14} />Sending…</>
              ) : (
                <><Send size={14} className="mr-2" />Send via DocuSign</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
